import * as THREE from 'three';
import type { Point, UnitState } from '../../../shared/game/world-types.js';
import { hitFlinch as tuning } from '../../../content/characters/rigs/hit-flinch.js';
import { hitKick, hitFrequency, type HitImpact, type HitKick, type HitMotion } from './character-hit.js';
import type { DeathRig } from './death-rig.js';
import type { UnitAnimator } from './unit-animator.js';

type Role = 'trunk' | 'head' | 'limb';
/** A bone that gives under a blow: its tilt off the animated pose (a rotation vector in the unit's frame) and how fast that changes. */
interface Part { bone: THREE.Object3D; role: Role; parent?: Part; tilt: THREE.Vector3; spin: THREE.Vector3; pull: THREE.Vector3; base: THREE.Quaternion; /** Spin a blow has yet to deliver. */ drive: THREE.Vector3 }
/** The first bone of a planted leg hanging from a part that gives: it keeps the animated orientation so the foot stays on the ground. */
interface Anchor { bone: THREE.Object3D; parent: Part; base: THREE.Quaternion }

/** Time constant (seconds) over which a blow's spin is delivered. */
const IMPACT_SECONDS = .015;
const turn = new THREE.Quaternion(), frame = new THREE.Quaternion(), inverse = new THREE.Quaternion();

/**
 * Physical hit reactions for any skeleton: each bone of a body's ragdoll above its feet carries a damped spring around the pose the animator
 * plays. A blow kicks the trunk away from it, and the head, arms (and any loose parts such as a tail or ears) lag behind by their inertia
 * and whip back, so the reaction always grows out of whatever the body was doing and settles back onto it. Root bones never move and legs
 * that reach the ground keep their animated orientation, so a living unit stays on its authoritative spot. Cost is a few vector operations
 * per bone while a body is shaken and nothing once it is still.
 */
export class HitSprings {
  private parts: Part[] = [];
  private anchors: Anchor[] = [];
  /** Parts that carry a planted leg: a body standing on them gives little and passes the blow up to its head. */
  private braced = new Set<Part>();
  private applied = false;
  private frequency = hitFrequency(.5);
  private head = 1;
  private fling = 1;
  private tremble = 0;
  private clock = 0;
  active = false;

  constructor(private rig: DeathRig, loose: readonly THREE.Object3D[] = []) {
    const segments = new Map(rig.segments.map(segment => [segment.bone, segment]));
    const kinds = new Map(rig.joints.map(joint => [joint.child, joint.kind]));
    const nearest = (bone: THREE.Object3D, among: ReadonlyMap<THREE.Object3D, unknown>) => {
      for (let parent = bone.parent; parent && parent !== rig.root; parent = parent.parent) if (among.has(parent)) return parent;
      return undefined;
    };
    // Which segments reach the ground, read from the pose the rig was built in: everything hanging below a part this low stays planted.
    rig.root.updateWorldMatrix(true, true);
    const toRoot = rig.root.matrixWorld.clone().invert();
    const height = (point: THREE.Vector3) => point.applyMatrix4(toRoot).y;
    const ends = new Map(rig.segments.map(segment => [segment.bone, height(segment.end instanceof THREE.Vector3
      ? segment.bone.localToWorld(segment.end.clone()) : segment.end.getWorldPosition(new THREE.Vector3()))]));
    const low = Math.min(...ends.values()), high = Math.max(...rig.segments.map(segment => height(segment.bone.getWorldPosition(new THREE.Vector3()))), ...ends.values());
    const lowest = new Map<THREE.Object3D, number>();
    for (const segment of rig.segments) for (let bone: THREE.Object3D | undefined = segment.bone; bone; bone = nearest(bone, segments))
      lowest.set(bone, Math.min(lowest.get(bone) ?? Infinity, ends.get(segment.bone)!));
    const planted = (bone: THREE.Object3D) => kinds.get(bone) !== 'fixed' && kinds.has(bone) && lowest.get(bone)! < low + (high - low) * .15;
    const hasChild = new Set(rig.segments.map(segment => nearest(segment.bone, segments)).filter(Boolean));
    const giving = new Map<THREE.Object3D, Role>();
    for (const segment of rig.segments) {
      const bone = segment.bone;
      if (!nearest(bone, segments) || planted(bone) || [...ancestors(bone, rig.root)].some(planted)) continue;
      giving.set(bone, kinds.get(bone) === 'fixed' ? hasChild.has(bone) ? 'trunk' : 'head' : 'limb');
    }
    for (const bone of loose) giving.set(bone, 'limb');
    const depth = (bone: THREE.Object3D) => [...ancestors(bone, rig.root)].length;
    const byBone = new Map<THREE.Object3D, Part>();
    for (const [bone, role] of [...giving].sort(([a], [b]) => depth(a) - depth(b))) {
      const above = nearest(bone, giving);
      const part: Part = { bone, role, parent: above && byBone.get(above), tilt: new THREE.Vector3(), spin: new THREE.Vector3(), pull: new THREE.Vector3(), base: new THREE.Quaternion(), drive: new THREE.Vector3() };
      byBone.set(bone, part); this.parts.push(part);
    }
    for (const segment of rig.segments) {
      const above = nearest(segment.bone, giving), hanging = nearest(segment.bone, segments);
      if (above && hanging && planted(segment.bone) && !planted(hanging)) this.anchors.push({ bone: segment.bone, parent: byBone.get(above)!, base: new THREE.Quaternion() });
    }
    for (const anchor of this.anchors) for (let part: Part | undefined = anchor.parent; part; part = part.parent) this.braced.add(part);
  }

  /** The lean of the first trunk part (radians, toward +x and +z in the unit's frame) and how fast it moves: what a body steps to catch. */
  get sway() {
    const trunk = this.parts.find(part => part.role === 'trunk');
    if (!trunk || !this.active) return undefined;
    // Up × tilt: where the top of the trunk has gone.
    return { x: -trunk.tilt.z, z: trunk.tilt.x, rate: Math.hypot(trunk.spin.x, trunk.spin.z) };
  }

  /** Throws a blow into the body. Kicks add to the motion already there, so a flurry keeps the body staggering. */
  kick(kick: HitKick) {
    this.frequency = kick.frequency; this.head = kick.head; this.fling = kick.fling;
    this.tremble = Math.max(this.tremble * .5, kick.tremble);
    // The trunk takes the blow; whatever hangs from it is left behind by its inertia (see `step`).
    const spin = new THREE.Vector3(kick.spin.x, kick.spin.y, kick.spin.z);
    for (const part of this.parts) {
      if (part.role === 'trunk' && !part.parent) part.drive.addScaledVector(spin, this.braced.has(part) ? tuning.braced : 1);
      if (part.role !== 'head') continue;
      let carried = false; for (let above = part.parent; above; above = above.parent) carried ||= this.braced.has(above);
      part.drive.addScaledVector(spin, (kick.head - 1) * .5 + (carried ? 1 - tuning.braced : 0));
    }
    this.active = this.parts.length > 0;
  }

  /** Advances the springs by `dt` seconds. */
  step(dt: number) {
    if (!this.active) return;
    const k = this.frequency ** 2, c = 2 * tuning.damping * this.frequency;
    for (let left = dt; left > 1e-6; left -= 1 / 90) {
      const h = Math.min(left, 1 / 90);
      this.clock += h; this.tremble *= Math.exp(-h * 4);
      let moving = false;
      for (const part of this.parts) {
        const pull = part.pull.copy(part.tilt).multiplyScalar(-k).addScaledVector(part.spin, -c);
        // A blow lands over a few hundredths of a second rather than in one frame.
        const delivered = part.drive.clone().multiplyScalar(1 - Math.exp(-h / IMPACT_SECONDS));
        part.drive.sub(delivered); pull.addScaledVector(delivered, 1 / h);
        if (part.parent) pull.addScaledVector(part.parent.pull, -this.lag(part.role));
        if (this.tremble > .01 && part.role !== 'limb') pull.add(new THREE.Vector3(Math.sin(this.clock * 64), 0, Math.cos(this.clock * 51)).multiplyScalar(this.tremble * this.frequency * (part.role === 'head' ? 1 : .5)));
        part.spin.addScaledVector(pull, h); part.tilt.addScaledVector(part.spin, h);
        if (part.tilt.length() > tuning.maxTilt) { part.tilt.setLength(tuning.maxTilt); part.spin.multiplyScalar(.5); }
        // A child feels its parent's acceleration as well as its own (see `lag`).
        if (part.parent) pull.add(part.parent.pull);
        moving ||= part.tilt.length() > tuning.rest || part.spin.length() > tuning.rest * this.frequency || part.drive.length() > tuning.rest;
      }
      if (!moving && this.tremble <= .01) { this.clear(); return; }
    }
  }

  /** How freely a part's own orientation ignores its parent's motion. */
  private lag(role: Role) { return Math.min(1, tuning.lag[role] * (role === 'limb' ? this.fling : role === 'head' ? Math.max(1, this.head * .6) : 1)); }

  /** Lays the springs over the pose just played. Call `restore` before the animator poses again. */
  apply() {
    if (!this.active) return;
    const rotations = new Map<THREE.Object3D, THREE.Quaternion>(), deltas = new Map<Part, THREE.Quaternion>();
    // Orientation of a bone in the unit's frame, from the animated pose.
    const inUnit = (bone: THREE.Object3D): THREE.Quaternion => {
      let rotation = rotations.get(bone);
      if (!rotation) { rotation = bone === this.rig.root || !bone.parent ? new THREE.Quaternion() : inUnit(bone.parent).clone().multiply(bone.quaternion); rotations.set(bone, rotation); }
      return rotation;
    };
    for (const part of [...this.parts, ...this.anchors]) inUnit(part.bone.parent!);
    for (const part of this.parts) {
      part.base.copy(part.bone.quaternion);
      const angle = part.tilt.length();
      turn.identity(); if (angle > 1e-6) turn.setFromAxisAngle(part.tilt.clone().divideScalar(angle), angle);
      // A tilt is about the unit's axes at the joint: carry it into the parent's frame, then onto the bone.
      frame.copy(rotations.get(part.bone.parent!)!); inverse.copy(frame).invert();
      part.bone.quaternion.premultiply(inverse.clone().multiply(turn).multiply(frame));
      deltas.set(part, (part.parent ? deltas.get(part.parent)!.clone() : new THREE.Quaternion()).multiply(turn));
    }
    for (const anchor of this.anchors) {
      anchor.base.copy(anchor.bone.quaternion);
      frame.copy(rotations.get(anchor.bone.parent!)!); inverse.copy(frame).invert();
      anchor.bone.quaternion.premultiply(inverse.clone().multiply(deltas.get(anchor.parent)!.clone().invert()).multiply(frame));
    }
    this.applied = true;
  }

  /** Samples the spring velocity without changing the displayed pose or consuming the pending impulse. */
  motion(): HitMotion | undefined {
    if (!this.active) return;
    const sample = 1 / 1000, applied = this.applied;
    const held = [...this.parts, ...this.anchors].map(part => ({ bone: part.bone, rotation: part.bone.quaternion.clone() }));
    const tilts = this.parts.map(part => part.tilt.clone());
    const centers = () => this.rig.segments.map(segment => {
      const start = segment.start ? segment.bone.localToWorld(segment.start.clone()) : segment.bone.getWorldPosition(new THREE.Vector3());
      const end = segment.end instanceof THREE.Vector3 ? segment.bone.localToWorld(segment.end.clone()) : segment.end.getWorldPosition(new THREE.Vector3());
      return { at: start.add(end).multiplyScalar(.5), rotation: segment.bone.getWorldQuaternion(new THREE.Quaternion()) };
    });
    this.rig.root.updateWorldMatrix(true, true);
    const before = centers();
    try {
      this.restore();
      // A lethal cue can arrive on the first dead frame: its undelivered spring kick becomes free-body momentum.
      for (const part of this.parts) part.tilt.addScaledVector(part.spin, sample).addScaledVector(part.drive, sample);
      this.apply(); this.rig.root.updateWorldMatrix(true, true);
      const after = centers();
      return new Map(this.rig.segments.map((segment, i) => {
        const delta = after[i].rotation.multiply(before[i].rotation.clone().invert()).normalize();
        if (delta.w < 0) delta.set(-delta.x, -delta.y, -delta.z, -delta.w);
        const axis = new THREE.Vector3(delta.x, delta.y, delta.z), length = axis.length();
        const angular = length > 1e-9 ? axis.multiplyScalar(2 * Math.atan2(length, delta.w) / (length * sample)) : axis;
        return [segment.bone, { linear: after[i].at.sub(before[i].at).divideScalar(sample), angular }];
      }));
    } finally {
      this.restore();
      this.parts.forEach((part, i) => part.tilt.copy(tilts[i]));
      for (const entry of held) entry.bone.quaternion.copy(entry.rotation);
      this.applied = applied; this.rig.root.updateWorldMatrix(true, true);
    }
  }

  /** Puts back the animated rotations `apply` changed, for an animator that does not write every bone each frame. */
  restore() {
    if (!this.applied) return;
    for (const part of [...this.parts, ...this.anchors]) part.bone.quaternion.copy(part.base);
    this.applied = false;
  }

  /** Stills the body at once. */
  clear() {
    for (const part of this.parts) { part.tilt.set(0, 0, 0); part.spin.set(0, 0, 0); part.pull.set(0, 0, 0); part.drive.set(0, 0, 0); }
    this.tremble = 0; this.active = false;
  }
}

function* ancestors(bone: THREE.Object3D, root: THREE.Object3D) { for (let parent = bone.parent; parent && parent !== root; parent = parent.parent) yield parent; }

/** What a body that steps to keep its balance reads of a stagger (the human animators). */
export interface HitSwaying { hitSway?: { x: number; z: number; rate: number } }

/**
 * Installs physical hit reactions on any unit animator (`createUnitAnimator` does it for every gameplay family). Blows reach it through the
 * animator's own `play('hit', …)` cue; it shakes the body only while the unit stands alive, and an animator that wants to keep its feet under the
 * stagger reads `hitSway`. `loose` adds parts outside the ragdoll that swing freely, such as a tail or ears.
 */
export function useUnitHitPhysics<T extends UnitAnimator>(animator: T, rig: DeathRig, loose: readonly THREE.Object3D[] = []): T {
  const springs = new HitSprings(rig, loose), swaying = animator as T & HitSwaying;
  const update = animator.update.bind(animator), play = animator.play.bind(animator), reset = animator.reset.bind(animator);
  let blows: { at: number; from?: Point; impact?: HitImpact }[] = [], time: number | undefined;
  const still = () => { springs.restore(); springs.clear(); blows = []; swaying.hitSway = undefined; };
  animator.play = (...args) => {
    const [kind, at, from, , impact] = args;
    if (kind === 'hit') blows.push({ at, from: from && { ...from }, impact });
    play(...args);
  };
  const deliver = (state: UnitState, position: Point, gameTime: number) => {
    for (const blow of blows.filter(blow => blow.at <= gameTime)) {
      if (gameTime - blow.at > tuning.stale) continue;
      const from = blow.from ?? { x: position.x, z: position.z + 1 };
      const toward = new THREE.Vector3(from.x - position.x, 0, from.z - position.z).applyQuaternion(rig.root.getWorldQuaternion(new THREE.Quaternion()).invert());
      springs.kick(hitKick(blow.impact, state.health.maxHp, toward));
    }
    blows = blows.filter(blow => blow.at > gameTime);
  };
  animator.hitMotion = (state, position, gameTime) => {
    if (time === undefined || gameTime < time) return;
    deliver(state, position, gameTime);
    return springs.motion();
  };
  animator.update = (...args) => {
    const [state, position, gameTime] = args as [UnitState, Point, number];
    if (time !== undefined && gameTime < time) { still(); time = undefined; }
    springs.restore();
    swaying.hitSway = springs.sway;
    update(...args);
    const standing = !state.health.dead && (animator.deathState ?? 'alive') === 'alive' && ((animator as { proneState?: string }).proneState ?? 'standing') === 'standing';
    if (!standing) { still(); time = gameTime; return; }
    const dt = time === undefined ? 0 : Math.min(.1, gameTime - time);
    deliver(state, position, gameTime);
    springs.step(dt); springs.apply();
    time = gameTime;
  };
  animator.reset = () => { still(); time = undefined; reset(); };
  return animator;
}
